'use client';

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { BugReportDialog } from './bug-report-dialog';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface BugReportContextValue {
  accountId: string;
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

const BugReportContext = createContext<BugReportContextValue | null>(null);

// accountId is passed by the server layout that mounts the provider (the
// admin app layout or the (tenant) owner-only mount) so the dialog works
// outside the team-account workspace context.
export function BugReportProvider({
  accountId,
  children,
}: {
  accountId: string;
  children?: ReactNode;
}) {
  // Deep-link handling: the comment notification email links to
  // /home/{slug}?bugReport={id} — that opens the dialog on the "My reports"
  // tab with the linked thread expanded. Reading window.location in the lazy
  // initializer (instead of useSearchParams) keeps the provider out of any
  // Suspense requirements; portals aren't server-rendered, so the client-only
  // initial value can't cause a hydration mismatch.
  const [state, setState] = useState<{
    isOpen: boolean;
    deepLinkReportId: string | null;
  }>(() => {
    if (typeof window === 'undefined') {
      return { isOpen: false, deepLinkReportId: null };
    }

    const reportId = new URLSearchParams(window.location.search).get(
      'bugReport',
    );

    const deepLinkReportId =
      reportId && UUID_RE.test(reportId) ? reportId : null;

    return { isOpen: Boolean(deepLinkReportId), deepLinkReportId };
  });

  // Strip the consumed param so refresh/back doesn't re-open the dialog.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (!params.has('bugReport')) {
      return;
    }

    params.delete('bugReport');
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}`,
    );
  }, []);

  const open = useCallback(
    () => setState((prev) => ({ ...prev, isOpen: true })),
    [],
  );

  const close = useCallback(
    () => setState({ isOpen: false, deepLinkReportId: null }),
    [],
  );

  const value = useMemo(
    () => ({ accountId, open, close, isOpen: state.isOpen }),
    [accountId, open, close, state.isOpen],
  );

  return (
    <BugReportContext.Provider value={value}>
      {children}
      <BugReportDialog
        accountId={accountId}
        open={state.isOpen}
        onOpenChange={(nextOpen) => {
          if (nextOpen) {
            open();
          } else {
            close();
          }
        }}
        initialReportId={state.deepLinkReportId}
      />
    </BugReportContext.Provider>
  );
}

export function useBugReport(): BugReportContextValue {
  const ctx = useContext(BugReportContext);
  if (!ctx) {
    throw new Error('useBugReport must be used within a BugReportProvider');
  }
  return ctx;
}
